<template>
  <div class="admin-repair-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('adminRepair.list.title')" :subtitle="$t('adminRepair.page.subtitle')" />

    <div class="admin-repair-layout">
      <!-- 左侧报修树 -->
      <div class="ds-panel ds-panel--tree">
        <community-repair-tree ref="communityRepairTree" @selectCommunity="handleSelectCommunity"
          @selectRepairSetting="handleSelectRepairSetting" @selectState="handleSelectState" />
      </div>

      <div class="admin-repair-main">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.repairId" clearable class="w-full"
                :placeholder="$t('adminRepair.search.repairId')" @keyup.enter.native="handleSearch" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.repairName" clearable class="w-full"
                :placeholder="$t('adminRepair.search.repairName')" @keyup.enter.native="handleSearch" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.tel" clearable class="w-full"
                :placeholder="$t('adminRepair.search.tel')" @keyup.enter.native="handleSearch" />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="handleReset">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="getList">
            <template #left>
              <span class="ds-panel__title">{{ $t('adminRepair.list.title') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('adminRepair.page.empty')" @retry="getList">
            <el-table-column prop="repairId" :label="$t('adminRepair.table.repairId')" align="center" width="150">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.repairId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="communityName" :label="$t('adminRepair.table.communityName')" align="center" min-width="130"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.communityName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="repairObjName" :label="$t('adminRepair.table.repairObjName')" align="center" min-width="130"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.repairObjName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="repairTypeName" :label="$t('adminRepair.table.repairTypeName')" align="center" width="120">
              <template slot-scope="scope">
                {{ scope.row.repairTypeName || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminRepair.table.maintenanceType')" align="center" width="120">
              <template slot-scope="scope">
                {{ getMaintenanceTypeName(scope.row.maintenanceType) }}
              </template>
            </el-table-column>
            <el-table-column prop="repairName" :label="$t('adminRepair.table.repairName')" align="center" width="100">
              <template slot-scope="scope">
                {{ scope.row.repairName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="tel" :label="$t('adminRepair.table.tel')" align="center" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.tel || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminRepair.table.appointmentTime')" align="center" width="170">
              <template slot-scope="scope">
                <div class="ds-num">{{ scope.row.appointmentTime || '-' }}</div>
                <div class="ds-text-muted ds-num" v-if="scope.row.timeout">~{{ scope.row.timeout }}</div>
              </template>
            </el-table-column>
            <el-table-column prop="createTime" :label="$t('adminRepair.table.createTime')" align="center" width="160">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="submitHours" :label="$t('adminRepair.table.submitHours')" align="center" width="100">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.submitHours || '0' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="finishTime" :label="$t('adminRepair.table.finishTime')" align="center" width="160">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.finishTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminRepair.table.state')" align="center" width="140">
              <template slot-scope="scope">
                <ds-status-tag :label="scope.row.stateName || '-'"
                  :text="scope.row.stateName"
                  :type="getStateType(scope.row.state)" />
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" align="center" width="100" fixed="right">
              <template slot-scope="scope">
                <el-button size="mini" type="primary" @click="handleDetail(scope.row)">
                  {{ $t('common.detail') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { listAdminOwnerRepairs } from '@/api/work/adminRepairApi'
import CommunityRepairTree from '@/components/work/CommunityRepairTree'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'AdminRepairList',
  components: {
    CommunityRepairTree,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        communityId: '',
        repairId: '',
        state: '',
        repairType: '',
        repairName: '',
        tel: ''
      },
      tableData: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getList()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          ...this.searchForm,
          page: this.pagination.current,
          row: this.pagination.size
        }
        const { data, total } = await listAdminOwnerRepairs(params)
        this.tableData = data
        this.pagination.total = total
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('adminRepair.fetchError')
        this.$message.error(this.$t('adminRepair.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm.repairId = ''
      this.searchForm.repairName = ''
      this.searchForm.tel = ''
      this.pagination.current = 1
      this.getList()
    },
    handleSelectCommunity({ communityId }) {
      this.searchForm.communityId = communityId
      this.searchForm.repairType = ''
      this.searchForm.state = ''
      this.pagination.current = 1
      this.getList()
    },
    handleSelectRepairSetting({ communityId, repairType }) {
      this.searchForm.communityId = communityId
      this.searchForm.repairType = repairType
      this.searchForm.state = ''
      this.pagination.current = 1
      this.getList()
    },
    handleSelectState({ communityId, repairType, state }) {
      this.searchForm.communityId = communityId
      this.searchForm.repairType = repairType
      this.searchForm.state = state
      this.pagination.current = 1
      this.getList()
    },
    handleDetail(row) {
      this.$router.push(`/views/work/adminRepairDetail?repairId=${row.repairId}`)
    },
    handleSizeChange(size) {
      this.pagination.size = size
      this.pagination.current = 1
      this.getList()
    },
    handleCurrentChange(current) {
      this.pagination.current = current
      this.getList()
    },
    getMaintenanceTypeName(type) {
      switch (type) {
        case '1001': return this.$t('adminRepair.maintenanceType.paid')
        case '1002': return this.$t('adminRepair.maintenanceType.free')
        case '1003': return this.$t('adminRepair.maintenanceType.withMaterial')
        case '1004': return this.$t('adminRepair.maintenanceType.withoutMaterial')
        default: return '--'
      }
    },
    getStateType(state) {
      switch (state) {
        case '1800': return 'warning'
        case '1801': return 'success'
        case '1802': return 'muted'
        default: return 'info'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.admin-repair-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧树 + 右侧内容布局 */
.admin-repair-layout {
  display: flex;
  align-items: flex-start;
  gap: var(--ds-spacing-lg);
}

.admin-repair-main {
  flex: 1;
  min-width: 0;
}

/* 左侧树面板 */
.ds-panel--tree {
  width: 250px;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
}

/* 面板标题 */
.ds-panel__title {
  font-size: var(--ds-font-size-lg);
  font-weight: 600;
  color: var(--ds-gray-800);
}

@media (max-width: 1200px) {
  .admin-repair-layout {
    flex-direction: column;
  }

  .ds-panel--tree {
    width: 100%;
  }
}
</style>
